import React, { useEffect, useRef, useState } from 'react';
import { Mail, ChevronDown, KeyRound } from 'lucide-react';
import { settingsAPI } from '../services/api';
import toast from '../utils/toast';
import { useOnboarding } from '../contexts/OnboardingContext';

const SECRET_KEY = 'RESEND_WEBHOOK_SECRET';

function ResendWebhookCard() {
  const [info, setInfo] = useState(null);       // { enabled, url, secret_configured, events }
  const [loadError, setLoadError] = useState('');
  const [secret, setSecret] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const urlRef = useRef(null);
  const { refresh: refreshOnboarding } = useOnboarding();

  const load = async () => {
    try {
      const res = await settingsAPI.getResendWebhook();
      setInfo(res);
      setLoadError('');
    } catch (err) {
      setLoadError(err.response?.data?.error || 'Could not load delivery tracking settings');
    }
  };

  useEffect(() => { load(); }, []);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(info.url);
      toast.success('Address copied');
    } catch {
      // Fallback: select the text so the user can copy it by hand
      if (urlRef.current) {
        urlRef.current.focus();
        urlRef.current.select();
      }
      toast('Press Ctrl+C to copy the selected address');
    }
  };

  const save = async (value) => {
    setError('');
    if (value && !value.startsWith('whsec_')) {
      setError('The signing secret must start with whsec_');
      return;
    }
    setBusy(true);
    try {
      await settingsAPI.update({ [SECRET_KEY]: value });
      setSecret('');
      toast.success(value ? 'Signing secret saved' : 'Signing secret cleared');
      refreshOnboarding();
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not save the signing secret');
    } finally {
      setBusy(false);
    }
  };

  const enabled = !!info?.enabled && !!info?.url;
  const events = info?.events || [];

  return (
    <div className="settings-card">
      <div className="settings-card-head">
        <span className="settings-card-icon"><Mail size={17} /></span>
        <div className="settings-card-titles">
          <h3>Delivery tracking (Resend)</h3>
          <p>Add your personal address below as a webhook in your Resend account so we can record deliveries, opens, clicks and bounces.</p>
        </div>
      </div>

      {loadError && <small className="setting-error">{loadError}</small>}

      {info && !enabled && (
        <small className="setting-note">Delivery tracking is not available yet</small>
      )}

      {info && enabled && (
        <>
          <div className="setting-row">
            <div className="setting-row-label">
              <label htmlFor="resend-webhook-url">Your webhook address</label>
              <small className="setting-help">
                Using several domains or several Resend accounts: add the same address once per Resend account.
              </small>
            </div>
            <div className="setting-row-control">
              <div className="setting-inline">
                <input id="resend-webhook-url" ref={urlRef} type="text" readOnly value={info.url} onFocus={(e) => e.target.select()} />
                <button type="button" className="btn btn-secondary" onClick={copy}>Copy</button>
              </div>
              <ol className="webhook-steps">
                <li>Open your Resend dashboard and go to Webhooks.</li>
                <li>Click Add endpoint.</li>
                <li>Paste the address above.</li>
                <li>Tick these events: {events.map((e) => <code key={e}>{e}</code>)}</li>
                <li>Click Save.</li>
              </ol>
            </div>
          </div>

          {/* Optional extra check: collapsed by default, the address above is all most users need */}
          <div className={`webhook-collapse webhook-advanced ${showAdvanced ? 'is-open' : ''}`}>
            <button
              type="button"
              className="webhook-collapse-toggle"
              onClick={() => setShowAdvanced((o) => !o)}
              aria-expanded={showAdvanced}
              aria-controls="resend-webhook-advanced"
            >
              <span className="webhook-collapse-title">
                <KeyRound size={14} />
                Advanced: signing secret (optional)
                {info.secret_configured && (
                  <span className="settings-pill-success">Saved</span>
                )}
              </span>
              <ChevronDown size={14} className="webhook-collapse-chevron" />
            </button>
            {showAdvanced && (
              <div id="resend-webhook-advanced" className="webhook-collapse-body">
                <p className="webhook-advanced-help">
                  When you create a webhook in Resend, Resend gives it a signing secret that starts with whsec_.
                  If you paste it here we also verify that every delivery event really came from Resend.
                  You can skip this: your personal address above already identifies your account.
                </p>
                <label className="webhook-field-label" htmlFor="resend-webhook-secret">
                  Signing secret (starts with whsec_)
                </label>
                <input
                  id="resend-webhook-secret"
                  type="password"
                  autoComplete="new-password"
                  value={secret}
                  onChange={(e) => setSecret(e.target.value)}
                  placeholder={info.secret_configured ? 'Saved - enter a new value to replace it' : 'whsec_...'}
                />
                {info.secret_unreadable && <small className="setting-error">Saved key can't be read, please enter it again</small>}
                {error && <small className="setting-error">{error}</small>}
                <div className="setting-actions">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    disabled={busy || !info.secret_configured}
                    onClick={() => save('')}
                  >
                    Clear
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={busy || !secret.trim()}
                    onClick={() => save(secret.trim())}
                  >
                    Save
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default ResendWebhookCard;
